import { useState } from "react";
import { Check, Copy, Phone, TriangleAlert } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { UiLang, Stop } from "../lib/types";

interface Props {
  /** Stoppene på ruta. Koordinatene deres er det operatøren spør om først. */
  stops: Stop[];
  lang: UiLang;
}

/**
 * Nødnumre, og hva man faktisk skal si.
 *
 * De tre numrene er lette å finne på nett. Det som ikke er lett å finne — og
 * som avgjør utfallet — er rekkefølgen på det man sier. Operatøren spør om
 * *posisjon* først, hver gang, fordi hjelp som ikke vet hvor den skal er ingen
 * hjelp. En tilreisende som ringer fra Skjeggedal og sier «vi er på en fjelltur i
 * Norge» har ikke svart på noe.
 *
 * Derfor står koordinatene her, klare til å leses opp. Appen har dem allerede
 * for hvert stopp, i desimalgrader, som er formatet nødetatene ber om. Det er
 * kanskje den mest konkret nyttige tingen hele appen kan gjøre, og den lå ubrukt
 * i datamodellen.
 *
 * Numrene og rådene er de norske. `120` er sjøredning, som er et eget nummer
 * fordi Hovedredningssentralen koordinerer redning på sjøen og i fjorder — og
 * flere av disse rutene går langs vann der det er det riktige nummeret.
 */

/** Numrene, i den rekkefølgen man trenger dem. */
const NUMBERS = [
  { number: "113", key: "medical" as const, urgent: true },
  { number: "112", key: "police" as const, urgent: true },
  { number: "110", key: "fire" as const, urgent: true },
  { number: "120", key: "sea" as const, urgent: true },
  { number: "116 117", key: "doctor" as const, urgent: false },
  { number: "175", key: "road" as const, urgent: false },
];

export function EmergencyCard({ stops, lang }: Props) {
  const t = dictionaries[lang];
  const [copied, setCopied] = useState<string | null>(null);

  /*
   * Stoppene med koordinater, i ruterekkefølge. Turstoppene er de viktigste
   * her — det er der man er langt fra vei og nærmest trenger å lese opp en
   * posisjon — men alle tas med, for man vet ikke hvor noe skjer.
   */
  const positions = [...stops].sort((a, b) => a.position - b.position);

  const copy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      // Utklippstavlen kan være sperret. Tallene står på skjermen uansett.
    }
  };

  return (
    <div className="emergency">
      <div className="emergency-head">
        <TriangleAlert size={15} />
        <div>
          <h3>{t.emergencyTitle}</h3>
          <p>{t.emergencySubtitle}</p>
        </div>
      </div>

      <ul className="emergency-numbers">
        {NUMBERS.map(({ number, key, urgent }) => (
          <li className={`emergency-number ${urgent ? "emergency-number--urgent" : ""}`} key={number}>
            {/*
              Ekte `tel:`-lenke. På en telefon er dette forskjellen mellom å lese
              et tall og å ringe, og i en nødssituasjon er det ikke en detalj.
            */}
            <a href={`tel:${number.replace(/\s/g, "")}`}>
              <Phone size={11} />
              <b>{number}</b>
            </a>
            <span>{t.emergencyWho[key]}</span>
          </li>
        ))}
      </ul>

      {/*
        Rekkefølgen på det man sier. Nummerert, fordi det *er* en rekkefølge —
        og fordi den som ringer skal kunne følge den med øynene mens hun snakker.
      */}
      <div className="emergency-what">
        <h4>{t.emergencyWhatTitle}</h4>
        <ol>
          {t.emergencySteps.map((step) => (
            <li key={step.title}>
              <b>{step.title}</b>
              <span>{step.detail}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="emergency-facts">
        <h4>{t.emergencyFactsTitle}</h4>
        <ul>
          {t.emergencyFacts.map((fact) => (
            <li key={fact}>{fact}</li>
          ))}
        </ul>
      </div>

      {/*
        Posisjonene, klare til å leses opp.

        Desimalgrader er formatet nødetatene ber om. Kopieringsknappen finnes
        fordi man i praksis vil lime posisjonen inn i en melding — og fordi det
        er lettere å lese opp fra en tekstmelding enn fra en tabell.
      */}
      {positions.length > 0 && (
        <div className="emergency-positions">
          <h4>{t.emergencyPositionsTitle}</h4>
          <p className="emergency-positions-hint">{t.emergencyPositionsHint}</p>
          <ul>
            {positions.map((stop) => {
              const coords = `${stop.lat.toFixed(5)}, ${stop.lon.toFixed(5)}`;
              return (
                <li key={stop.id}>
                  <b>{stop.name}</b>
                  <code>{coords}</code>
                  <button
                    type="button"
                    className="emergency-copy no-print"
                    onClick={() => copy(`${stop.name}: ${coords}`, stop.id)}
                    aria-label={`${t.copy} ${stop.name}`}
                  >
                    {copied === stop.id ? <Check size={11} /> : <Copy size={11} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
